import { useLanguage } from '@/contexts/language-context';
import { formatMoney } from '@/lib/utils';
import type { SharedQuoteDto } from '@/lib/api/public-quotes';

import { formatDateTime } from '@/routes/quotes/helpers';

interface Props {
  quote: SharedQuoteDto;
}

function statusLabel(
  status: SharedQuoteDto['status'],
  t: ReturnType<typeof useLanguage>['t'],
): string {
  switch (status) {
    case 'accepted':
      return t.quoteStatusAccepted;
    case 'declined':
      return t.quoteStatusDeclined;
    default:
      return t.quoteStatusPending;
  }
}

function statusColorClass(status: SharedQuoteDto['status']): string {
  switch (status) {
    case 'accepted':
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    case 'declined':
      return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
    default:
      return 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400';
  }
}

// Read-only, invoice-style rendering of the sanitized public payload. Mirrors
// the look of `routes/quotes/quote-document.tsx` without depending on the full
// `QuoteDto` shape.
export function SharedQuoteView({ quote }: Props) {
  const { t } = useLanguage();

  const meta: string[] = [];
  if (quote.sent_at)
    meta.push(`${t.sharedQuoteSent} ${formatDateTime(quote.sent_at)}`);
  meta.push(`${t.sharedQuoteExpires} ${formatDateTime(quote.expires_at)}`);

  return (
    <div
      data-testid="shared-quote-view"
      className="rounded-lg border bg-background p-5 flex flex-col gap-5"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          {quote.logo_url && (
            <img
              src={quote.logo_url}
              alt={t.sharedDocumentLogoAlt}
              data-testid="shared-quote-logo"
              className="w-12 h-12 rounded-lg object-contain border bg-background shrink-0"
            />
          )}
          <div className="min-w-0">
            <h1 className="text-lg font-semibold leading-tight">
              {quote.company_name}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t.sharedQuoteFor} {quote.customer_name}
            </p>
            {quote.job_name && (
              <p className="text-sm text-muted-foreground truncate">
                {quote.job_name}
              </p>
            )}
          </div>
        </div>
        <span
          className={`text-[11px] font-medium px-2 py-0.5 rounded-full shrink-0 ${statusColorClass(quote.status)}`}
          data-testid="shared-quote-status"
        >
          {statusLabel(quote.status, t)}
        </span>
      </div>
      <p className="text-xs text-muted-foreground -mt-3">{meta.join(' · ')}</p>

      {/* Line items */}
      <div>
        {quote.line_items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-3">
            {t.quoteNoLineItems}
          </p>
        ) : (
          <>
            <div className="flex items-center justify-between text-[11px] font-medium uppercase tracking-wide text-muted-foreground border-b pb-1.5">
              <span>{t.quoteLineItemDescription}</span>
              <span>{t.quoteLineItemAmount}</span>
            </div>
            <ul className="divide-y">
              {quote.line_items.map((item, i) => (
                <li
                  // Display-only rows, no server id.
                  // eslint-disable-next-line @eslint-react/no-array-index-key
                  key={`${item.description}-${item.amount}-${i}`}
                  className="flex items-start justify-between gap-4 py-2 text-sm"
                >
                  <span className="min-w-0">{item.description}</span>
                  <span className="shrink-0 tabular-nums">
                    {formatMoney(item.amount)}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}

        <div className="mt-2 flex items-center justify-between border-t pt-2 text-sm font-semibold">
          <span>{t.quoteTotal}</span>
          <span className="tabular-nums" data-testid="shared-quote-total">
            {formatMoney(quote.total_amount)}
          </span>
        </div>
      </div>

      {/* Notes */}
      {quote.notes && (
        <div className="flex flex-col gap-1">
          <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            {t.quoteNotes}
          </p>
          <p
            className="text-sm whitespace-pre-wrap"
            data-testid="shared-quote-notes"
          >
            {quote.notes}
          </p>
        </div>
      )}
    </div>
  );
}
